import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../contexts/AuthContext";

import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import createAddAthleteSchema from "./schemas/addAthleteSchema";

import AddAthleteInput from "./components/AddAthleteInput";
import Button from "../../components/Button";

import { addAthlete } from "./api/athleteRequests";
import { useTranslation } from "../../i18n/TranslationProvider";

const ReviewRoster = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const rows = state.parsedData || [];
    const { userId: authUserId } = useAuth();
    const userId = state?.userId ?? authUserId;
    const { t } = useTranslation();

    const addAthleteSchema = useMemo(() => createAddAthleteSchema(t), [t]);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(addAthleteSchema),
        defaultValues: { athletes: rows },
    });

    const { fields, remove } = useFieldArray({
        control,
        name: "athletes",
    });

    const onSubmit = async (data) => {
        const athletes = data.athletes;
        const payloads = athletes.map((athlete) => ({
            ...athlete,
            userId,
        }));

        try {
            await Promise.all(payloads.map((p) => addAthlete(p)));
            navigate("/athletes");
        } catch (err) {
            console.error("Bulk Upload Failed", err);
        }
    };

    return (
        <>
            <div className="custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2 ">
                <div className="text-center mx-auto mb-6">
                    <h4 className="text-lg md:text-2xl font-semibold mb-2">
                        {t("athletes.reviewRosterHeading")}
                    </h4>
                    <p>
                        {t("athletes.reviewRosterSubtext")}
                    </p>
                </div>
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-4">
                        {fields.map((field, index) => (
                            <div
                                key={field.id}
                                className="border-primary border p-4 rounded-md"
                            >
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-semibold">
                                        {t("athletes.athleteNumber", { number: index + 1 })}
                                    </h4>
                                    <button
                                        type="button"
                                        className="text-red-600 hover:underline text-sm"
                                        onClick={() => remove(index)}
                                    >
                                        {t("common.remove")}
                                    </button>
                                </div>

                                <AddAthleteInput
                                    index={index}
                                    register={register}
                                    errors={errors}
                                />
                            </div>
                        ))}
                    </div>
                    <Button
                        type="submit"
                        isLoading={isSubmitting}
                        className="w-full mt-4 mb-2"
                    >
                        {t("athletes.confirmUploadButton", { count: fields.length })}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => navigate(-1)}
                        className="w-full"
                    >
                        {t("auth.back")}
                    </Button>
                    <div className="mt-4 px-2 flex justify-between items-center">
                        <p>{t("auth.needHelp")}</p>
                        <h6
                            className="text-teal-600 cursor-pointer"
                            onClick={() => navigate("/help")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    navigate("/help");
                                }
                            }}
                        >
                            {t("auth.contactUs")}
                        </h6>
                    </div>
                </form>
            </div>
        </>
    );
};

export default ReviewRoster;